import React, { Component, ErrorInfo, ReactNode } from 'react';

import GenericError from '../../pages/Errors/GenericError';

interface IErrorBoundaryProps {
  children: ReactNode;
  /**
   * Quando muda, o boundary se recupera sozinho. Passe `location.key` para
   * que navegar (ou voltar) limpe o erro.
   */
  resetKey?: string;
  /** `null` faz o boundary engolir o erro e não renderizar nada. */
  fallback?: ReactNode;
}

interface IErrorBoundaryState {
  hasError: boolean;
}

/**
 * Sem isto, qualquer exceção de renderização desmonta a árvore inteira e o
 * usuário vê uma página totalmente em branco — cenário comum aqui, porque
 * várias telas leem dados que ficam `undefined` quando a requisição falha.
 */
class ErrorBoundary extends Component<
  IErrorBoundaryProps,
  IErrorBoundaryState
> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): IErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo.componentStack);
  }

  componentDidUpdate(prevProps: IErrorBoundaryProps): void {
    const { resetKey } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.resetKey !== resetKey) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    const { children, fallback } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return fallback !== undefined ? fallback : <GenericError />;
    }

    return children;
  }
}

export default ErrorBoundary;
